import { FlyToInterpolator, type MapViewState, type PickingInfo, WebMercatorViewport } from "@deck.gl/core";
import { PathLayer, PolygonLayer, ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import DeckGL, { type DeckGLRef } from "@deck.gl/react";
import { Zone } from "@fairfleet/geotab";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import Map from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { mapClickAction, VERTEX_LAYER_ID, ZONE_LAYER_ID } from "../utils/mapClick";
import { colorToRgba, FALLBACK_ZONE_COLOR } from "../utils/zoneColor";
import { boundsOf, geotabPointsToLngLat, LngLat } from "../utils/zoneGeometry";

const MAP_STYLE = "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";
const GERMANY: MapViewState = { longitude: 10.4, latitude: 51.2, zoom: 5.5, pitch: 0, bearing: 0 };
const LABEL_MIN_ZOOM = 13;
const FILL_ALPHA = 70;
const HIDDEN_FILL_ALPHA = 25;
const DRAFT_COLOR: [number, number, number, number] = [211, 47, 47, 255];
const FIT_PADDING = 60;
const FIT_MAX_ZOOM = 17;
/** Points of a double-click this close on screen count as one. */
const FINISH_TOLERANCE_PX = 5;
/** Web-Mercator metres per pixel at zoom 0 on the equator. */
const METERS_PER_PIXEL_Z0 = 156_543.033_92;
/** Space the floating panel covers on the left; fitted shapes stay clear of it. */
const PANEL_OFFSET = 380;

interface ZoneShape {
  id: string;
  name: string;
  polygon: LngLat[];
  fill: [number, number, number, number];
  line: [number, number, number, number];
  center: LngLat;
}

export interface MapFocus {
  bounds: [LngLat, LngLat];
  /** Changes whenever the same bounds should be flown to again. */
  key: number;
}

interface Props {
  zones: Zone[];
  selectedZoneId?: string;
  /** Hidden from the zone layer while its draft is shown. */
  editingZoneId?: string;
  editing: boolean;
  draftPoints: LngLat[];
  draftColor?: Zone["fillColor"];
  drawing: boolean;
  addressPin: LngLat | null;
  focus: MapFocus | null;
  onZoneClick(id: string): void;
  onMapClick(point: LngLat): void;
  /** `toleranceM`: how close in metres two points may be before they count as one. */
  onFinishDrawing(toleranceM: number): void;
  onVertexMove(index: number, point: LngLat): void;
  onVertexRemove(index: number): void;
}

function toShape(zone: Zone): ZoneShape | null {
  const polygon = geotabPointsToLngLat(zone.points);
  if (polygon.length < 3) return null;
  const color = zone.fillColor ?? FALLBACK_ZONE_COLOR;
  const bounds = boundsOf(polygon)!;
  const center: LngLat =
    zone.centroidLongitude && zone.centroidLatitude
      ? [zone.centroidLongitude, zone.centroidLatitude]
      : [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
  return {
    id: zone.id,
    name: zone.name ?? "",
    polygon,
    fill: colorToRgba(color, zone.displayed === false ? HIDDEN_FILL_ALPHA : FILL_ALPHA),
    line: colorToRgba(color, 255),
    center,
  };
}

export default function ZonesMap({
  zones,
  selectedZoneId,
  editingZoneId,
  editing,
  draftPoints,
  draftColor,
  drawing,
  addressPin,
  focus,
  onZoneClick,
  onMapClick,
  onFinishDrawing,
  onVertexMove,
  onVertexRemove,
}: Props) {
  const deckRef = useRef<DeckGLRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewState, setViewState] = useState<MapViewState>(GERMANY);
  const [hoveredVertex, setHoveredVertex] = useState<number | null>(null);
  const [draggedVertex, setDraggedVertex] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!focus || !container) return;
    const { clientWidth: width, clientHeight: height } = container;
    if (width <= 0 || height <= 0) return;
    const [[west, south], [east, north]] = focus.bounds;
    const fitted = new WebMercatorViewport({ width, height }).fitBounds(
      [
        [west, south],
        [east, north],
      ],
      {
        padding: {
          top: FIT_PADDING,
          bottom: FIT_PADDING,
          right: FIT_PADDING,
          left: Math.min(PANEL_OFFSET, width / 2) + FIT_PADDING,
        },
        maxZoom: FIT_MAX_ZOOM,
      },
    );
    setViewState({
      longitude: fitted.longitude,
      latitude: fitted.latitude,
      zoom: fitted.zoom,
      pitch: 0,
      bearing: 0,
      transitionDuration: 600,
      transitionInterpolator: new FlyToInterpolator(),
    });
  }, [focus]);

  const shapes = useMemo(
    () => zones.filter(zone => zone.id !== editingZoneId).map(toShape).filter((s): s is ZoneShape => s !== null),
    [zones, editingZoneId],
  );

  const draftRgba = colorToRgba(draftColor ?? FALLBACK_ZONE_COLOR, 255);
  const vertices = draftPoints.map((position, index) => ({ position, index }));

  const layers = [
    new PolygonLayer<ZoneShape>({
      id: ZONE_LAYER_ID,
      data: shapes,
      getPolygon: s => s.polygon,
      getFillColor: s => s.fill,
      getLineColor: s => s.line,
      getLineWidth: s => (s.id === selectedZoneId ? 4 : 2),
      lineWidthUnits: "pixels",
      pickable: !editing,
      autoHighlight: !editing,
      highlightColor: [255, 255, 255, 60],
      updateTriggers: { getLineWidth: selectedZoneId },
    }),
    new TextLayer<ZoneShape>({
      id: "zone-labels",
      data: shapes,
      visible: viewState.zoom >= LABEL_MIN_ZOOM,
      getPosition: s => s.center,
      getText: s => s.name,
      getSize: 12,
      characterSet: "auto",
      background: true,
      getBackgroundColor: [255, 255, 255, 210],
      backgroundPadding: [4, 2],
      fontFamily: "Roboto, sans-serif",
    }),
    !drawing && draftPoints.length >= 3
      ? new PolygonLayer<{ polygon: LngLat[] }>({
          id: "draft-polygon",
          data: [{ polygon: draftPoints }],
          getPolygon: d => d.polygon,
          getFillColor: [draftRgba[0], draftRgba[1], draftRgba[2], FILL_ALPHA],
          getLineColor: DRAFT_COLOR,
          getLineWidth: 2,
          lineWidthUnits: "pixels",
          updateTriggers: { getFillColor: draftRgba.join() },
        })
      : null,
    drawing && draftPoints.length >= 2
      ? new PathLayer<{ path: LngLat[] }>({
          id: "draft-path",
          data: [{ path: draftPoints }],
          getPath: d => d.path,
          getColor: DRAFT_COLOR,
          getWidth: 2,
          widthUnits: "pixels",
        })
      : null,
    editing && draftPoints.length > 0
      ? new ScatterplotLayer<{ position: LngLat; index: number }>({
          id: VERTEX_LAYER_ID,
          data: vertices,
          getPosition: v => v.position,
          getRadius: v => (v.index === hoveredVertex || v.index === draggedVertex ? 8 : 6),
          radiusUnits: "pixels",
          getFillColor: [255, 255, 255, 255],
          getLineColor: DRAFT_COLOR,
          lineWidthUnits: "pixels",
          getLineWidth: 2,
          stroked: true,
          pickable: true,
          onHover: info => setHoveredVertex(info.object ? info.object.index : null),
          onDragStart: info => setDraggedVertex(info.object ? info.object.index : null),
          onDrag: info => {
            if (draggedVertex !== null && info.coordinate) {
              onVertexMove(draggedVertex, [info.coordinate[0], info.coordinate[1]]);
            }
          },
          onDragEnd: () => setDraggedVertex(null),
          updateTriggers: { getRadius: [hoveredVertex, draggedVertex] },
        })
      : null,
    addressPin
      ? new ScatterplotLayer<LngLat>({
          id: "address-pin",
          data: [addressPin],
          getPosition: p => p,
          getRadius: 5,
          radiusUnits: "pixels",
          getFillColor: DRAFT_COLOR,
        })
      : null,
  ];

  const finishTolerance = () =>
    (FINISH_TOLERANCE_PX * METERS_PER_PIXEL_Z0 * Math.cos((viewState.latitude * Math.PI) / 180)) / 2 ** viewState.zoom;

  const handleClick = (info: PickingInfo) => {
    const action = mapClickAction({
      drawing,
      editing,
      layerId: info.object ? info.layer?.id : undefined,
      vertexIndex: (info.object as { index?: number } | undefined)?.index,
      pointCount: draftPoints.length,
    });
    if (action === "finish") onFinishDrawing(finishTolerance());
    else if (action === "add" && info.coordinate) onMapClick([info.coordinate[0], info.coordinate[1]]);
    else if (action === "openZone") onZoneClick((info.object as ZoneShape).id);
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!editing) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const picked = deckRef.current?.pickObject({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      layerIds: [VERTEX_LAYER_ID],
    });
    if (picked?.object) onVertexRemove((picked.object as { index: number }).index);
  };

  const overVertex = hoveredVertex !== null || draggedVertex !== null;

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
      onDoubleClick={() => drawing && onFinishDrawing(finishTolerance())}
      onContextMenu={handleContextMenu}
    >
      <DeckGL
        ref={deckRef}
        deviceProps={{ type: "webgl" }}
        viewState={viewState}
        onViewStateChange={({ viewState: next }) => setViewState(next as MapViewState)}
        controller={{ dragRotate: false, touchRotate: false, doubleClickZoom: !drawing, dragPan: !overVertex }}
        layers={layers}
        onClick={handleClick}
        getCursor={({ isDragging, isHovering }) =>
          overVertex ? "move" : drawing ? "crosshair" : isDragging ? "grabbing" : isHovering ? "pointer" : "grab"
        }
        style={{ width: "100%", height: "100%" }}
      >
        <Map mapStyle={MAP_STYLE} />
      </DeckGL>
    </div>
  );
}
